import React from 'react';

import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { FormattedMessage } from 'react-intl';

import LoadingModal from 'src/components/navigation/LoadingModal';
import { getLocalUserId, useLogout } from 'src/features/auth';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { useListUserMemberships } from 'src/features/profile/api/useListUserMemberships';
import { detailsMessages } from 'src/features/profile/utils';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHead,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';

import Header from './Header';
import NavigationSidebar from './NavigationSidebar';

const AppNavigationWrapper = ({ children }: { children: React.ReactNode }) => {
    const { sidePanelState, forceSidePanel, forceLeftStaticPanel } = useSidePanel();
    const userId = getLocalUserId();
    const [isSidePanelOpen, setIsSidePanelOpen] = React.useState(false);
    const {
        parkingName,
        parkingId,
        parkingRole,
        setParkingId,
        setParkingName,
        setParkingRole,
        setParkingCurrency,
        parkingCurrency,
    } = useParking();
    const {
        data: userMemberships,
        isLoading: isLoadingUserMemberships,
        isError: isErrorMemberships,
    } = useListUserMemberships({ userId });

    const { mutate: logout } = useLogout({
        onSuccess: () => {
            window.location.reload();
        },
    });

    // If we don't have a initial parking id/name set the first occurance of the memberships list
    React.useEffect(() => {
        if (!userMemberships) return;
        let validMembership = userMemberships.find((m) => m.parking.id === parkingId);
        if (!validMembership) {
            validMembership = userMemberships[0];
        }
        setParkingId(validMembership?.parking.id);
        setParkingName(validMembership?.parking.name);
        setParkingRole(validMembership?.role);
        setParkingCurrency(validMembership?.parking.default_currency);
    }, [userMemberships]);

    React.useEffect(() => {
        const isOpen = Object.values(sidePanelState).some((value) => value);
        setIsSidePanelOpen(isOpen);
    }, [sidePanelState]);

    if (isLoadingUserMemberships) {
        return <LoadingModal isLoading={isLoadingUserMemberships} />;
    }

    if (isErrorMemberships || userMemberships?.length == 0 || !userMemberships) {
        return (
            <Dialog open={true} modal>
                <DialogContent className="" showOverlay={false} showCloseButton={false}>
                    <DialogHeader>
                        <DialogHead>
                            <DialogTitle>
                                <FormattedMessage {...membershipMessages.notAMember} />{' '}
                            </DialogTitle>
                        </DialogHead>
                    </DialogHeader>
                    <DialogSeparator />
                    <DialogBody>
                        <div className="flex gap-4">
                            <SquareIcon icon={faExclamationTriangle} />
                            <p className="">
                                <FormattedMessage {...membershipMessages.notAMemberDescription} />
                            </p>
                        </div>
                    </DialogBody>
                    <DialogSeparator />
                    <DialogFooter>
                        <Button variant="destructive" onClick={() => logout(undefined)}>
                            <FormattedMessage {...detailsMessages.logout} />
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <>
            <NavigationSidebar />
            <div
                className={cn(
                    `transition-padding relative my-16 flex min-h-[calc(100vh-8rem)] w-full flex-col gap-6 rounded-3xl bg-white p-4 pt-8 shadow-md duration-300 ease-in-out sm:my-4 sm:ml-20 sm:min-h-[calc(100vh-2rem)] sm:max-w-[calc(100%-5rem)] sm:rounded-l-3xl sm:rounded-r-none sm:p-8 dark:bg-gray-900`,
                    isSidePanelOpen && 'sm:pr-104',
                    forceSidePanel && 'md:pr-104',
                    forceLeftStaticPanel && 'sm:pl-64',
                )}
            >
                {forceLeftStaticPanel && (
                    <div className="absolute inset-y-0 left-56 z-50 hidden w-px bg-gray-200 sm:block" />
                )}
                <Header userMemberships={userMemberships} />
                {children}
            </div>
        </>
    );
};

export default AppNavigationWrapper;
